import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../../i18n";
import { FehlerBand, Knopf } from "../../bausteine";
import { Deckblatt, useDeckblatt } from "../../bausteine/Deckblatt";
import { Kundenwahl } from "../../bausteine/Kundenwahl";
import { OrtAnlegen } from "../../bausteine/OrtAnlegen";
import { Datum } from "../../bausteine/Zeitraum";
import { Wochenwahl } from "../../bausteine/Wochenwahl";
import { hindernisText } from "../../bausteine/Verteilen";
import { useKataloge, useKunde, useLeistungsarten } from "../../daten/haken";
import { useAufnahmeLage, useOrtsaufnahmeAnlegen } from "../../daten/ortsaufnahme";
import {
  fehltNoch, lagetage, vorschlagsfrage, type Ortsaufnahmeentwurf, type Wannart,
} from "../../daten/ortsaufnahmeregeln";
import { einsatzHindernisse, vorschlagFuerEinsatz } from "../../daten/einteilen";
import { kuerzelVon } from "../../daten/verteilen";
import { heuteImBetrieb, tagPlus } from "../../daten/betriebstag";
import type { Kunde } from "../../daten/typen";
import { useSitzung } from "../../schale/Sitzung";
import { useTelefon } from "../../schale/geraet";
import { ortsaufnahmeSchliessen, useOrtsaufnahmeStand, type Ortsaufnahmevorgabe } from "../../schale/ortsaufnahme";
import { useRuf } from "../../schale/Ruf";
import { AufmassAnlegen } from "../handy/AufmassAnlegen";
import "../../stile/ortsaufnahme.css";

/* ══════════════════════════════════════════════════════════════════
   Das Fenster „Ortsaufnahme anlegen" (08.10.2026, Paket ortsaufnahme).

   Luca: *„Guck dir bitte die Erfassung der Ortsaufnahmen an. Wenn ich das
   selber mache, mach ein vernünftiges Pop-up, sodass man ein Aufmaß
   anlegen und eine Ortsaufnahme aufnehmen kann. Ich muss Ortsaufnahmen
   auch verteilen können. Wenn ich über ‚Neu' und ‚Aufmaß' gehe, muss direkt
   das Pop-up aufgehen. Dann brauche ich nicht extra dieses
   Ortsaufnahmen-Modul."*

   ```
   Ortsaufnahme anlegen                                   [Schließen]
   ─────────────────────────────────────────────────────────────────
   Kunde        [ Kranbau Ostfriesland GmbH            Ändern ]
   Einsatzort   [ Solarpark Süd                       ⌄ ] + Neuer Ort
   Was          [ PV-Reinigung                         ⌄ ]
   Wann         ( Noch offen | Tag | Woche )  [ Do 15.10. ] 09:00–10:00
   Wer          [ Unter „Offen" in Einteilen           ⌄ ]
                Vorschlag: Martin Lenger · hat als Einziger die Qualifikation
   Hinweis      [                                         ]
   ─────────────────────────────────────────────────────────────────
   Was fehlt …         Abbrechen  Anlegen und gleich aufnehmen  [Anlegen]
   ```

   **Ein Fenster in der Mitte, wie jedes Deckblatt** (`bausteine/Deckblatt`):
   Esc, Grund, Nachfrage bei angefangenem Entwurf, Fokus hinein und zurück.
   Kunde über die Kundenwahl (mit „Kunde anlegen" als Interessent), Ort aus
   der Akte oder neu (`OrtAnlegen`), Leistung aus dem Preisstamm (nur, wofür
   eine Ortsaufnahme Sinn hat), Termin als Tag mit Uhrzeit oder als Woche.

   **Wer** ist freiwillig: leer heißt „unter Offen in Einteilen" — dort
   wird verteilt. Der Vorschlag kommt aus derselben Regel wie in Einteilen
   (`vorschlagFuerEinsatz`): nur mit Grund, nach Qualifikation, frei, Last.

   **Zwei Wege am Ende.** „Anlegen" gibt sie der gewählten Person oder legt
   sie unter „Offen". „Anlegen und gleich aufnehmen" ist der Fall, in dem
   man es selbst macht: Sie gehört dann einem selbst, und die Erfassung
   (`/aufnahmen/$id`) geht sofort auf.

   **Am Telefon bleibt das Blatt von unten** (`handy/AufmassAnlegen`): Dort
   steht der Techniker vor der Anlage und misst gleich.
   ══════════════════════════════════════════════════════════════════ */

export function OrtsaufnahmeFenster() {
  const t = useT();
  const stand = useOrtsaufnahmeStand();
  const telefon = useTelefon();
  const [schmutzig, setzeSchmutzig] = useState(false);
  useEffect(() => { if (stand.offen) setzeSchmutzig(false); }, [stand.offen, stand.nr]);

  /* Am Telefon dasselbe Blatt wie bisher hinter dem Kreis — der Techniker
     legt sie sich vor Ort an und misst gleich. */
  /* Nur offen eingehängt: Das Blatt fragt Leistungsarten und Kataloge ab,
     und das soll nicht auf jedem Schirm im Hintergrund geschehen. */
  if (telefon) return stand.offen ? <AufmassAnlegen offen schliessen={ortsaufnahmeSchliessen} /> : null;

  const v = stand.vorgabe;
  return (
    <Deckblatt
      offen={stand.offen}
      kompakt
      weite={680}
      schmutzig={schmutzig}
      schliessen={ortsaufnahmeSchliessen}
      titel={t("ortsaufnahmeAnlegen.titel")}
      untertitel={v.vorgang
        ? t("ortsaufnahmeAnlegen.unterVorgang", { vorgang: v.vorgang })
        : t("ortsaufnahmeAnlegen.unter")}
    >
      {stand.offen ? <Formular key={stand.nr} vorgabe={v} geaendert={setzeSchmutzig} /> : null}
    </Deckblatt>
  );
}

function leererEntwurf(v: Ortsaufnahmevorgabe): Ortsaufnahmeentwurf {
  return {
    kundeNr: v.kundeNr ?? "", ort: v.ort ?? "", leistungId: v.leistungsartId ?? "", positionId: v.positionId ?? "",
    wann: "offen", tag: null, von: "09:00", bis: "10:00", woche: null, person: "", hinweis: "",
  };
}

function Formular({ vorgabe, geaendert }: { vorgabe: Ortsaufnahmevorgabe; geaendert: (ja: boolean) => void }) {
  const t = useT();
  const ruf = useRuf();
  const gehZu = useNavigate();
  const { schliessen } = useDeckblatt();
  const { ich } = useSitzung();
  const heute = heuteImBetrieb();
  const anlegen = useOrtsaufnahmeAnlegen();
  const { data: leistungen } = useLeistungsarten();
  const kataloge = useKataloge();

  const [e, setzeE] = useState<Ortsaufnahmeentwurf>(() => leererEntwurf(vorgabe));
  const [kunde, setzeKunde] = useState<Kunde | null>(null);
  const [ortAnlegen, setzeOrtAnlegen] = useState(false);
  const bestellt = Boolean(e.positionId);
  const setze = (teil: Partial<Ortsaufnahmeentwurf>) => { setzeE((alt) => ({ ...alt, ...teil })); geaendert(true); };

  /* Die Akte des Kunden bringt die Orte — und, wenn das Fenster mit einer
     Kundennummer aufging, den Kunden für die Kundenwahl. */
  const akte = useKunde(e.kundeNr);
  useEffect(() => {
    if (!kunde && akte.data && akte.data.nr === e.kundeNr) setzeKunde(akte.data);
  }, [akte.data, e.kundeNr, kunde]);
  const orte = (akte.data?.orte ?? []).filter((o) => o.aktiv !== false);

  /* Nur, wofür eine Ortsaufnahme Sinn hat (Haken im Preisstamm) — dieselbe
     Auswahl wie am Telefon (`AufmassAnlegen`). */
  const waehlbar = (leistungen?.zeilen ?? []).filter((l) => l.aktiv && l.ortsaufnahme);
  const einheitVon = (code: string): string =>
    (kataloge.data?.zaehlgroessen ?? []).find((g) => g.code === code)?.einheit || code;
  /* Eine Leistung allein muss niemand wählen (Regel 11). */
  useEffect(() => {
    if (!bestellt && !e.leistungId && waehlbar.length === 1) setzeE((alt) => ({ ...alt, leistungId: waehlbar[0]!.id }));
  }, [bestellt, e.leistungId, waehlbar]);

  /* ── Wer in Frage kommt — dieselbe Lage wie in Einteilen ── */
  const tage = lagetage(e, heute);
  const lage = useAufnahmeLage(tage.von, tage.bis, e.leistungId);
  const leute = useMemo(() => (lage.data?.leute ?? []).filter((p) => p.disponierbar !== false), [lage.data]);
  const frage = vorschlagsfrage(e, lage.data?.verlangt ?? [], heute);
  const vorschlag = lage.data && !e.person
    ? vorschlagFuerEinsatz(frage, leute, lage.data.band, lage.data.einsaetze) : null;
  const gewaehlt = leute.find((p) => p.id === e.person) ?? null;
  const hindernisse = gewaehlt && lage.data ? einsatzHindernisse(frage, gewaehlt, lage.data.band, lage.data.einsaetze) : [];

  const fehlt = fehltNoch(e);
  /* „Gleich aufnehmen" heißt: Ich mache es selbst. Steht ein anderer unter
     „Wer", widerspräche es sich — der Knopf sagt dann, warum nicht. */
  const selbstGesperrt = fehlt ?? (e.person && e.person !== ich?.id ? "ortsaufnahmeAnlegen.selbstNurOhneAndere" : null);

  const los = (selbst: boolean) => {
    if (fehlt) return;
    /* Aus dem Angebot heraus: danach verknüpft — solange der Kunde der des
       Angebots geblieben ist (ein anderer Kunde gehört nicht in dieses Angebot). */
    const zuordnenAn = vorgabe.zuordnenAn && e.kundeNr === (vorgabe.kundeNr ?? "") ? vorgabe.zuordnenAn : undefined;
    anlegen.mutate({ entwurf: e, selbst: selbst ? ich?.id ?? null : null, zuordnenAn }, {
      onSuccess: (a) => {
        geaendert(false);
        ortsaufnahmeSchliessen();
        if (selbst && a.id) {
          void gehZu({ to: "/aufnahmen/$id", params: { id: a.id } });
          return;
        }
        ruf(e.person ? "ortsaufnahmeAngelegtFuer" : "ortsaufnahmeAngelegtOffen",
          e.person ? { name: gewaehlt?.name ?? "" } : undefined);
      },
    });
  };

  const wannArten: Wannart[] = ["offen", "tag", "kw"];
  const leistungWort = waehlbar.find((l) => l.id === e.leistungId)?.label ?? vorgabe.leistung ?? "";

  return (
    <div className="deck-form oa-form">
      <div className="deck-form-leib">
        <div className="oa-leib">
          {anlegen.isError ? <FehlerBand fehler={anlegen.error} /> : null}

          {/* ── Kunde, Ort, Was — bei einer Bestellung stehen sie an der Position ── */}
          {bestellt ? (
            <div className="oa-zeile">
              <span className="feld-schild">{t("ortsaufnahmeAnlegen.ausVorgang")}</span>
              <p className="oa-wert">
                <b>{vorgabe.kunde ?? kunde?.name ?? ""}</b>
                {[vorgabe.ort, leistungWort].filter(Boolean).length
                  ? <span> · {[vorgabe.ort, leistungWort].filter(Boolean).join(" · ")}</span> : null}
              </p>
            </div>
          ) : (
            <>
              <div className="oa-zeile">
                <label className="feld-schild" htmlFor="oa-kunde">{t("ortsaufnahmeAnlegen.kunde")}</label>
                <div className="oa-feld">
                  <Kundenwahl
                    id="oa-kunde"
                    wert={kunde}
                    art="interessent"
                    aendern={(k) => { setzeKunde(k); setze({ kundeNr: k?.nr ?? "", ort: "" }); }}
                  />
                </div>
              </div>

              <div className="oa-zeile">
                <label className="feld-schild" htmlFor="oa-ort">{t("ortsaufnahmeAnlegen.ort")}</label>
                <div className="oa-reihe">
                  <select
                    id="oa-ort"
                    className="feld"
                    value={e.ort}
                    disabled={!kunde}
                    title={kunde ? undefined : t("ortsaufnahmeAnlegen.ortErstKunde")}
                    onChange={(ev) => setze({ ort: ev.target.value })}
                  >
                    <option value="">{t(orte.length ? "ortsaufnahmeAnlegen.ortOffen" : "ortsaufnahmeAnlegen.ortKeiner")}</option>
                    {orte.map((o) => (
                      <option key={o.uuid ?? o.id ?? o.name} value={o.name}>
                        {[o.name, o.ort].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `)}
                      </option>
                    ))}
                  </select>
                  <Knopf
                    disabled={!kunde}
                    title={kunde ? undefined : t("ortsaufnahmeAnlegen.ortErstKunde")}
                    onClick={() => setzeOrtAnlegen(true)}
                  >
                    {t("ortsaufnahmeAnlegen.ortNeu")}
                  </Knopf>
                </div>
              </div>

              <div className="oa-zeile">
                <label className="feld-schild" htmlFor="oa-was">{t("ortsaufnahmeAnlegen.was")}</label>
                {waehlbar.length ? (
                  <select id="oa-was" className="feld" value={e.leistungId} onChange={(ev) => setze({ leistungId: ev.target.value })}>
                    <option value="">{t("ortsaufnahmeAnlegen.wasWaehlen")}</option>
                    {waehlbar.map((l) => (
                      <option key={l.id} value={l.id}>
                        {t("ortsaufnahmeAnlegen.wasZeile", { leistung: l.label, einheit: einheitVon(l.groesse) })}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="oa-satz">{t("aufmassAnlegen.keineLeistung")}</p>
                )}
              </div>
            </>
          )}

          {/* ── Wann: noch offen, ein Tag mit Uhrzeit, eine Woche ── */}
          <div className="oa-zeile">
            <span className="feld-schild" id="oa-wann">{t("ortsaufnahmeAnlegen.wann")}</span>
            <div className="oa-wann">
              <div className="sg-segmente" role="group" aria-labelledby="oa-wann">
                {wannArten.map((w) => (
                  <button
                    key={w}
                    type="button"
                    className={`chip${e.wann === w ? " an" : ""}`}
                    aria-pressed={e.wann === w}
                    onClick={() => setze({
                      wann: w,
                      ...(w === "tag" && !e.tag ? { tag: tagPlus(heute, 1) } : {}),
                    })}
                  >
                    {t(`ortsaufnahmeAnlegen.wannArt.${w}`)}
                  </button>
                ))}
              </div>
              {e.wann === "tag" ? (
                <div className="oa-reihe">
                  <Datum
                    id="oa-tag"
                    wert={e.tag}
                    richtung="vor"
                    schild={t("ortsaufnahmeAnlegen.tag")}
                    aendern={(tag) => setze({ tag })}
                  />
                  <input className="feld oa-uhr" type="time" aria-label={t("ortsaufnahmeAnlegen.von")}
                    value={e.von} onChange={(ev) => setze({ von: ev.target.value })} />
                  <span className="oa-bis" aria-hidden="true">–</span>
                  <input className="feld oa-uhr" type="time" aria-label={t("ortsaufnahmeAnlegen.bis")}
                    value={e.bis} onChange={(ev) => setze({ bis: ev.target.value })} />
                </div>
              ) : e.wann === "kw" ? (
                <Wochenwahl
                  id="oa-woche"
                  wert={e.woche}
                  leerWort={t("ortsaufnahmeAnlegen.wocheWaehlen")}
                  aendern={(w) => setze({ woche: w.von })}
                />
              ) : null}
            </div>
            <p className="oa-satz">
              {t(e.wann === "tag"
                ? (e.person ? "ortsaufnahmeAnlegen.wannTagMitPerson" : "ortsaufnahmeAnlegen.wannTagOffen")
                : e.wann === "kw"
                  ? "ortsaufnahmeAnlegen.wannKw"
                  : "ortsaufnahmeAnlegen.wannOffen")}
            </p>
          </div>

          {/* ── Wer: freiwillig; leer heißt „unter Offen in Einteilen" ── */}
          <div className="oa-zeile">
            <label className="feld-schild" htmlFor="oa-wer">{t("ortsaufnahmeAnlegen.wer")}</label>
            <select id="oa-wer" className="feld" value={e.person} onChange={(ev) => setze({ person: ev.target.value })}>
              <option value="">{t("ortsaufnahmeAnlegen.werOffen")}</option>
              {leute.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {vorschlag ? (
              <button type="button" className="oa-vorschlag" onClick={() => setze({ person: vorschlag.person.id })}
                title={t("einteilen.vorschlagSatz", { name: vorschlag.person.name, grund: t(`einteilen.grund.${vorschlag.grund}`) })}>
                <span className="oa-kreis" aria-hidden="true">{kuerzelVon(vorschlag.person)}</span>
                <span>{t("ortsaufnahmeAnlegen.vorschlag", { name: vorschlag.person.name, grund: t(`einteilen.grund.${vorschlag.grund}`) })}</span>
              </button>
            ) : null}
            {hindernisse.length && lage.data ? (
              <p className="oa-satz oa-acht">
                {hindernisse.map((h) => hindernisText(h, lage.data!, t)).join(" · ")}
              </p>
            ) : null}
          </div>

          <div className="oa-zeile">
            <label className="feld-schild" htmlFor="oa-hinweis">{t("ortsaufnahmeAnlegen.hinweis")}</label>
            <textarea
              id="oa-hinweis"
              className="feld oa-hinweis"
              rows={3}
              maxLength={2000}
              value={e.hinweis}
              placeholder={t("ortsaufnahmeAnlegen.hinweisPlatz")}
              onChange={(ev) => setze({ hinweis: ev.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="deck-fuss">
        {fehlt ? <span className="weg">{t(fehlt)}</span> : null}
        <Knopf art="stumm" onClick={schliessen}>{t("allgemein.abbrechen")}</Knopf>
        <Knopf
          disabled={Boolean(selbstGesperrt) || anlegen.isPending}
          title={selbstGesperrt ? t(selbstGesperrt) : t("ortsaufnahmeAnlegen.selbstSatz")}
          onClick={() => los(true)}
        >
          {t("ortsaufnahmeAnlegen.selbst")}
        </Knopf>
        <Knopf
          art="haupt"
          disabled={Boolean(fehlt) || anlegen.isPending}
          title={fehlt ? t(fehlt) : undefined}
          onClick={() => los(false)}
        >
          {t(anlegen.isPending ? "ortsaufnahmeAnlegen.laeuft" : "ortsaufnahmeAnlegen.anlegen")}
        </Knopf>
      </div>

      {/* Der neue Ort entsteht in der Akte und steht danach gewählt da (Regel 11). */}
      <OrtAnlegen
        offen={ortAnlegen}
        kunde={kunde}
        erster={orte.length === 0}
        schliessen={() => setzeOrtAnlegen(false)}
        angelegt={(o) => { setze({ ort: o.name }); setzeOrtAnlegen(false); void akte.refetch(); }}
      />
    </div>
  );
}
